/**
 * JoinPhoneContainer — Phone entry screen for the group invite flow
 * Route: /groups/join/:gid/phone?inviter=<uid>
 *
 * Real Firebase phone auth (sends the OTP).
 */
import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  brand,
  fontSans,
  spacing,
  radii,
  shadows,
} from '../../style/splytBrand';
import useJoinPageChrome from './useJoinPageChrome';
import { sendCode, createRecaptchaVerifier } from './firebaseAuth';
import { readInviteParams, buildInviteQuery } from './inviteHelpers';
import { track, EVENTS, SCREEN } from '../../analytics/track';
import JoinFooter from './JoinFooter';
import useKeyboardPinnedFooter from './useKeyboardPinnedFooter';

// ─── Subcomponents ────────────────────────────────────────────────────────────

function Wordmark({ size = 22, color = brand.caviar }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        fontFamily: fontSans,
        fontWeight: 700,
        fontSize: size,
        letterSpacing: '-0.04em',
        color,
      }}
    >
      <svg width={size * 0.95} height={size * 0.95} viewBox="0 0 24 24" fill="none">
        <path
          d="M6 4h8a4 4 0 010 8H10a4 4 0 010 8h-4"
          stroke={color}
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      splyt
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    // svh, matching the wrapper below. `100vh` is the LARGE viewport on iOS
    // Safari (toolbars hidden), so it made the document ~80 to 100px taller
    // than the visible area and the page scrolled for no reason.
    minHeight: '100vh',
    '@supports (height: 100svh)': {
      minHeight: '100svh',
    },
    backgroundColor: brand.marsh,
    fontFamily: fontSans,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },

  // Mobile layout
  mobileWrapper: {
    display: 'flex',
    flexDirection: 'column',
    // border-box so the padding below stays INSIDE the viewport-sized column;
    // with the default content-box the column is taller than the screen and the
    // bottom-aligned footer falls under the browser toolbar.
    boxSizing: 'border-box',
    minHeight: '100vh',
    // svh, not dvh: iOS Chrome treats dvh as the bars-hidden height and lays
    // the footer out behind the bottom toolbar (same rationale as the name and
    // handoff screens).
    '@supports (height: 100svh)': {
      minHeight: '100svh',
    },
    padding: `${spacing.md}px ${spacing.md + 4}px 0`,
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },

  // Desktop layout
  desktopWrapper: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '24px 40px 40px',
    },
  },

  desktopHeader: {
    width: '100%',
    maxWidth: 1080,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 28,
  },

  desktopCard: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: brand.surface,
    borderRadius: radii.card,
    padding: 36,
    boxShadow: shadows.card,
    border: `1px solid ${brand.marshNeutral}`,
  },

  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    paddingBottom: spacing.md,
  },

  desktopContent: {
    display: 'flex',
    flexDirection: 'column',
  },

  // Breadcrumb chip
  breadcrumb: {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 8,
    padding: '6px 12px 6px 8px',
    backgroundColor: brand.marsh75,
    borderRadius: radii.pill,
    border: `1px solid ${brand.marshNeutral}`,
    marginBottom: 20,
  },

  breadcrumbText: {
    fontWeight: 600,
    fontSize: 12,
    lineHeight: 1,
    color: brand.caviar75,
  },

  breadcrumbName: {
    color: brand.caviar,
  },

  breadcrumbClose: {
    cursor: 'pointer',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
  },

  // Title
  title: {
    margin: 0,
    fontWeight: 700,
    letterSpacing: '-0.025em',
    color: brand.caviar,
  },

  titleMobile: {
    fontSize: 26,
    lineHeight: 1.1,
  },

  titleDesktop: {
    fontSize: 30,
    lineHeight: 1.1,
  },

  subtitle: {
    margin: '6px 0 22px',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.45,
    color: brand.caviar75,
  },

  // Phone input row
  phoneInputRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 8,
  },

  countryPicker: {
    appearance: 'none',
    cursor: 'pointer',
    // Never the item that gets squeezed once the input wrapper can shrink.
    flexShrink: 0,
    backgroundColor: brand.surface,
    border: `1.5px solid ${brand.marshNeutral}`,
    borderRadius: radii.input,
    padding: '0 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: fontSans,
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 1,
    color: brand.caviar,
  },

  countryFlag: {
    fontSize: 22,
  },

  phoneInputWrapper: {
    flex: 1,
    // A flex item's default min-width:auto is its content's min-content width,
    // and an <input>'s is its intrinsic ~20-character width (about 212px in
    // WebKit at 17px SF). That floor is wider than the 335px column on a 375pt
    // iPhone, so the wrapper overflowed the row by 21px and .content's
    // overflow:auto clipped its right border, corner and focus ring flush at
    // the column edge — the box looked like it ran off the screen. minWidth:0
    // lets it shrink; measured 0px overflow at every iPhone width afterwards.
    minWidth: 0,
    backgroundColor: brand.surface,
    borderRadius: radii.input,
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  },

  phoneInputWrapperDefault: {
    border: `1.5px solid ${brand.marshNeutral}`,
  },

  phoneInputWrapperFocused: {
    border: `1.5px solid ${brand.caviar}`,
    boxShadow: shadows.inputFocus,
  },

  phoneInput: {
    flex: 1,
    // Keeps the input shrinkable inside the wrapper, so a longer placeholder or
    // a future wrapper change can't reintroduce the overflow above.
    minWidth: 0,
    width: '100%',
    border: 'none',
    outline: 'none',
    fontFamily: fontSans,
    fontWeight: 500,
    fontSize: 17,
    lineHeight: 1,
    color: brand.caviar,
    backgroundColor: 'transparent',
    fontVariantNumeric: 'tabular-nums',
    '&::placeholder': {
      color: brand.caviar50,
    },
  },

  // Privacy note
  privacyNote: {
    marginTop: 18,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '12px 14px',
    backgroundColor: brand.marsh75,
    borderRadius: radii.lg,
    border: `1px solid ${brand.marshNeutral}`,
  },

  privacyEmoji: {
    fontSize: 16,
    lineHeight: 1,
    marginTop: 1,
  },

  privacyText: {
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.45,
    color: brand.caviar75,
  },

  // Footer
  footer: {
    flexShrink: 0,
    padding: `${spacing.sm}px 0`,
    paddingBottom: `calc(${spacing.lg}px + env(safe-area-inset-bottom, 0px))`,
  },

  desktopFooter: {
    marginTop: spacing.lg,
  },

  // Primary button
  primaryButton: {
    width: '100%',
    backgroundColor: brand.caviar,
    color: brand.surface,
    border: 'none',
    borderRadius: radii.pill,
    padding: '16px 22px',
    fontFamily: fontSans,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1.1,
    letterSpacing: '-0.005em',
    boxShadow: shadows.primaryButton,
    cursor: 'pointer',
    transition: 'transform 120ms ease, opacity 150ms ease',
    '&:hover:not(:disabled)': {
      transform: 'scale(0.985)',
    },
    '&:active:not(:disabled)': {
      transform: 'scale(0.98)',
    },
    '&:disabled': {
      backgroundColor: brand.caviar25,
      boxShadow: 'none',
      cursor: 'not-allowed',
    },
  },

  // Terms caption
  termsCaption: {
    margin: '14px 0 0',
    textAlign: 'center',
    fontWeight: 400,
    fontSize: 11,
    lineHeight: 1.45,
    color: brand.caviar50,
  },

  termsLink: {
    color: brand.caviar75,
    textDecoration: 'underline',
    cursor: 'pointer',
  },

  // Error message
  errorMessage: {
    marginTop: 16,
    padding: '12px 14px',
    backgroundColor: `${brand.ketchup}15`,
    borderRadius: radii.lg,
    fontWeight: 500,
    fontSize: 13,
    lineHeight: 1.4,
    color: brand.ketchup,
    textAlign: 'center',
  },

  // Invisible reCAPTCHA container
  recaptchaContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    opacity: 0,
    pointerEvents: 'none',
    zIndex: -1,
  },

  // Header link
  headerLink: {
    color: brand.blueberry,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.3,
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    borderBottom: `1.5px solid ${brand.blueberry25}`,
    padding: 0,
    paddingBottom: 1,
    cursor: 'pointer',
    fontFamily: fontSans,
  },
}));

// ─── Main Component ───────────────────────────────────────────────────────────

export default function JoinPhoneContainer() {
  useJoinPageChrome();
  const classes = useStyles();
  const { gid } = useParams();
  const history = useHistory();
  const location = useLocation();

  const { inviter, inviteCode, utmSource } = readInviteParams(location.search);
  const query = buildInviteQuery({ inviter, inviteCode, utmSource });

  // Lifts the CTA above the software keyboard on iOS (see the hook's header).
  const { footerRef, footerHeight, pinned } = useKeyboardPinnedFooter();
  const inputRef = useRef(null);

  // The wall of the group loop: phone entry
  useEffect(function trackPhoneAuthScreen() {
    track(EVENTS.SCREEN_VIEWED, { screen_name: SCREEN.PHONE_AUTH, group_id: gid });
  }, [gid]);

  // Form state
  const [phone, setPhone] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // RecaptchaVerifier - matches existing SignUpModal pattern exactly
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  useEffect(() => {
    setRecaptchaVerifier(createRecaptchaVerifier('join-recaptcha-container'));
  }, []);

  // Format phone number for display (US format)
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(digits);
    if (error) setError(''); // Clear error on input
  };

  const isValidPhone = phone.length >= 10;

  const handleSubmit = async () => {
    if (!isValidPhone || isLoading) return;

    // Normalize to E.164 format: strip all non-digits and prepend country code
    const digits = phone.replace(/\D/g, '');
    const e164Phone = `+1${digits}`;

    // Validate E.164 format: must be at least 11 characters (+1 + 10 digits for US)
    if (e164Phone.length < 12) {
      setError("That phone number doesn't look right.");
      return;
    }

    setIsLoading(true);
    setError('');

    // Close the keyboard BEFORE requesting the code: the invisible reCAPTCHA can
    // escalate to a visible challenge, which is centered on the layout viewport
    // and would be half-hidden behind an open keyboard with no way to scroll to it.
    inputRef.current?.blur();

    try {
      // sendCode stores confirmation in module state, no need to pass it through router
      await sendCode(e164Phone, recaptchaVerifier, {
        displayPhone: formatPhone(phone),
      });

      // Navigate to verify; gid is in the path, inviter rides along in the query.
      history.push(`/groups/join/${gid}/verify${query}`);
    } catch (err) {
      console.error('Failed to send code:', err);
      setError(err.message || 'Failed to send code. Please try again.');
      setIsLoading(false);
    }
  };

  // Shared content renderer
  const renderContent = (isMobile) => (
    <>
      <h2
        className={`${classes.title} ${
          isMobile ? classes.titleMobile : classes.titleDesktop
        }`}
      >
        What&apos;s your number?
      </h2>

      <p className={classes.subtitle}>
        We&apos;ll text you a code to confirm. No spam, ever.
      </p>

      {/* Phone input */}
      <div className={classes.phoneInputRow}>
        <button type="button" className={classes.countryPicker}>
          <span className={classes.countryFlag}>🇺🇸</span>
          <span>+1</span>
          <svg width="10" height="10" viewBox="0 0 10 10">
            <path
              d="M2 4l3 3 3-3"
              stroke={brand.caviar50}
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div
          className={`${classes.phoneInputWrapper} ${
            isFocused
              ? classes.phoneInputWrapperFocused
              : classes.phoneInputWrapperDefault
          }`}
        >
          {/* enterkeyhint is lowercase on purpose: React 16 passes unknown
              lowercase attributes straight through but warns on camelCase ones. */}
          <input
            ref={inputRef}
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            // eslint-disable-next-line react/no-unknown-property
            enterkeyhint="go"
            className={classes.phoneInput}
            placeholder="(555) 555-5555"
            value={formatPhone(phone)}
            onChange={handlePhoneChange}
            onKeyDown={(e) => {
              // Android numeric keyboards show a Go key; iOS number pads do not.
              if (e.key === 'Enter') handleSubmit();
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </div>
      </div>

      {/* Privacy note */}
      <div className={classes.privacyNote}>
        <span className={classes.privacyEmoji}>🔒</span>
        <div className={classes.privacyText}>
          Your number is only used to verify it&apos;s you and notify you of new
          splyts. We won&apos;t text spam, ever.
        </div>
      </div>

      {/* Error message */}
      {error && <div className={classes.errorMessage}>{error}</div>}
    </>
  );

  // Footer content
  const renderFooter = () => (
    <>
      <button
        type="button"
        className={classes.primaryButton}
        disabled={!isValidPhone || isLoading}
        onClick={handleSubmit}
        // iOS fires blur before click: without this the first tap on the pinned
        // bar only closes the keyboard and the button slides out from under the finger.
        onMouseDown={(e) => e.preventDefault()}
      >
        {isLoading ? 'Sending...' : 'Text me a code'}
      </button>
      <p className={classes.termsCaption}>
        By continuing, you agree to Splyt&apos;s{' '}
        <span className={classes.termsLink}>Terms</span> &{' '}
        <span className={classes.termsLink}>Privacy</span>.
      </p>
    </>
  );

  return (
    <div className={classes.root}>
      {/* Mobile layout */}
      <div className={classes.mobileWrapper}>
        <div className={classes.content} style={{ paddingBottom: footerHeight }}>
          {renderContent(true)}
        </div>
        <JoinFooter footerRef={footerRef} pinned={pinned}>
          {renderFooter()}
        </JoinFooter>
      </div>

      {/* Desktop layout */}
      <div className={classes.desktopWrapper}>
        <div className={classes.desktopHeader}>
          <Wordmark size={22} />
          <button type="button" className={classes.headerLink}>
            Already have Splyt?
          </button>
        </div>
        <div className={classes.desktopCard}>
          <div className={classes.desktopContent}>{renderContent(false)}</div>
          <div className={classes.desktopFooter}>{renderFooter()}</div>
        </div>
      </div>

      {/* reCAPTCHA container - required for Firebase phone auth */}
      <span id="join-recaptcha-container" />
    </div>
  );
}
